import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { MessagingChannelAddress } from "../../commonSubSchemas/messagingChannelAddress.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    VersionEventActorFieldsWithDefaults,
    VersionEventActorSchemaTypes,
} from "../../types/versionEventActor.d.js";
import { type DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "VersionEventActor" as const;

/**
 * Kinds of actor that can trigger a version transition. Spelled `actorType` rather than `type`,
 * which mongoose reads as a path-type declaration.
 *
 * TODO: Add "system" — `{ actorType: "system"; subsystem: string }` — so internal writes (periodic
 * jobs, reconcilers, warning creation) name the subsystem responsible instead of leaving `changedBy`
 * absent. That means supplying a subsystem at every internal SCD write site, so it is its own piece
 * of work. "agent" and "technician" arms are likewise expected eventually.
 */
export const VERSION_EVENT_ACTOR_TYPES = ["user", "messagingChannel", "accessKey"] as const;

export type VersionEventActorType = (typeof VERSION_EVENT_ACTOR_TYPES)[number];

const IDENTITY_FIELDS = ["descopeUserId", "address", "accessKeyName"] as const;

type IdentityField = (typeof IDENTITY_FIELDS)[number];

/** The one identity field each arm owns. A new arm means a new entry here. */
const IDENTITY_FIELD_BY_ACTOR_TYPE = {
    user: "descopeUserId",
    messagingChannel: "address",
    accessKey: "accessKeyName",
} as const satisfies Record<VersionEventActorType, IdentityField>;

/**
 * The arm's identity must be present and every other arm's absent, so an audit record can never name a
 * `user` by phone number or carry no identity at all. Shared by the Mongoose paths and the DTO
 * refinement below, so the two layers cannot disagree about what a well-formed actor is.
 */
function actorPairingIssues(actor: {
    actorType?: VersionEventActorType;
    descopeUserId?: unknown;
    address?: unknown;
    accessKeyName?: unknown;
}): { field: IdentityField; message: string }[] {
    // No arm to check against — a partial update DTO may legitimately omit it.
    if (actor.actorType === undefined) return [];

    const owned = IDENTITY_FIELD_BY_ACTOR_TYPE[actor.actorType];
    return IDENTITY_FIELDS.flatMap((field) => {
        const present = actor[field] !== undefined && actor[field] !== null;
        if (field === owned && !present) {
            return [{ field, message: `${field} is required for a "${actor.actorType}" actor` }];
        }
        if (field !== owned && present) {
            return [{ field, message: `${field} does not belong to a "${actor.actorType}" actor` }];
        }
        return [];
    });
}

/** `this` is the subdocument under validation, so these have to be `function`s rather than arrows. */
function isIdentityFieldOwned(this: VersionEventActor, field: IdentityField): boolean {
    return this.actorType !== undefined && IDENTITY_FIELD_BY_ACTOR_TYPE[this.actorType] === field;
}

function identityFieldBelongsToArm(this: VersionEventActor, field: IdentityField, value: unknown): boolean {
    return value === undefined || value === null || isIdentityFieldOwned.call(this, field);
}

/**
 * Who triggered a version transition. Every stored actor carries `actorType`, so later arms are
 * additive — no backfill of existing events.
 *
 * A discriminated union on `actorType`, spelled as optional fields because the decorators compose
 * per-field validators only:
 *
 * - `{ actorType: "user"; descopeUserId: string }`
 * - `{ actorType: "messagingChannel"; address: MessagingChannelAddress }`
 * - `{ actorType: "accessKey"; accessKeyName: string }`
 *
 * The pairing is enforced at both layers — conditionally-required Mongoose paths and a `superRefine`
 * over the generated DTO validators — so neither a missing nor a foreign identity can be stored. Read
 * an identity field only after narrowing on `actorType`.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<VersionEventActor>>(...addSchemaKeyHook(SCHEMA_KEY))
export class VersionEventActor extends SchemaBase<VersionEventActor, VersionEventActorFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.enum(VERSION_EVENT_ACTOR_TYPES) })
    @prop({ type: () => String, required: true, enum: VERSION_EVENT_ACTOR_TYPES })
    public actorType!: VersionEventActorType;

    /** Descope user id, an opaque string such as `U2abc...`. The `user` arm's identity. */
    @validate({ zodValidator: z.string().min(1).optional() })
    @prop({
        type: () => String,
        required: function (this: VersionEventActor) {
            return isIdentityFieldOwned.call(this, "descopeUserId");
        },
        validate: {
            validator: function (this: VersionEventActor, value: unknown) {
                return identityFieldBelongsToArm.call(this, "descopeUserId", value);
            },
            message: 'descopeUserId belongs to a "user" actor',
        },
    })
    public descopeUserId?: string;

    /** The endpoint that sent the triggering message, channel included. The `messagingChannel` arm's identity. */
    @validate({ childClass: MessagingChannelAddress, childClassTransform: (object) => object.optional() })
    @prop({
        type: () => MessagingChannelAddress,
        _id: false,
        required: function (this: VersionEventActor) {
            return isIdentityFieldOwned.call(this, "address");
        },
        validate: {
            validator: function (this: VersionEventActor, value: unknown) {
                return identityFieldBelongsToArm.call(this, "address", value);
            },
            message: 'address belongs to a "messagingChannel" actor',
        },
    })
    public address?: MessagingChannelAddress;

    /** Name of the Descope access key, from its `accessKeyName` custom claim. The `accessKey` arm's identity. */
    @validate({ zodValidator: z.string().min(1).optional() })
    @prop({
        type: () => String,
        required: function (this: VersionEventActor) {
            return isIdentityFieldOwned.call(this, "accessKeyName");
        },
        validate: {
            validator: function (this: VersionEventActor, value: unknown) {
                return identityFieldBelongsToArm.call(this, "accessKeyName", value);
            },
            message: 'accessKeyName belongs to an "accessKey" actor',
        },
    })
    public accessKeyName?: string;

    static getters = {} as const satisfies Record<string, (self: VersionEventActor) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<VersionEventActorSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<VersionEventActor, VersionEventActorFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

/**
 * The decorator system has no object-level hook, so the arm↔identity rule is layered on after generation
 * — over every variant, since a malformed actor must not pass any of them.
 */
const generatedValidators = getZodSchemaFromClass(VersionEventActor) as unknown as Record<string, z.ZodType>;

VersionEventActor.dtoValidators = Object.fromEntries(
    Object.entries(generatedValidators).map(([variant, validator]) => [
        variant,
        validator.superRefine((actor, ctx) => {
            actorPairingIssues(actor as Parameters<typeof actorPairingIssues>[0]).forEach(({ field, message }) => {
                ctx.addIssue({ code: "custom", path: [field], message });
            });
        }),
    ]),
) as unknown as DtoValidatorTypes<VersionEventActorSchemaTypes>;
